/** 判断 URL 路径是否是视频资源，忽略查询参数上的图片处理命令。 */
function isVideoSourceUrl(url: string): boolean {
  const cleanUrl = url.split(/[?#]/)[0].split('!')[0].toLowerCase();
  return /\.(mp4|mov|avi|mkv|flv|wmv|webm|m4v)$/.test(cleanUrl);
}

/** 判断 URL 是否已经包含视频截帧命令。 */
function hasVideoFrameCommand(url: string): boolean {
  return /(?:[?&|])vframe\//i.test(url);
}

/** 清理错误套在视频原地址上的图片处理命令，避免视频被当成图片封面加载。 */
function normalizeVideoCoverSourceUrl(url: string): string {
  if (!isVideoSourceUrl(url) || hasVideoFrameCommand(url)) return url;
  const query = url.split('?')[1] ?? '';
  if (!/(imageView2|imageMogr2|watermark\/)/i.test(query)) return url;
  return url.split('?')[0].split('!')[0];
}

/** 移除 URL 上已有的 vframe 截帧命令，保留其余查询参数。 */
function stripVideoFrameCommand(url: string): string {
  if (!hasVideoFrameCommand(url)) return url;
  const [path, queryPart] = url.split('?');
  if (!queryPart) return path;
  const remaining = queryPart.split('&').filter((part) => !/^vframe\//i.test(part));
  return remaining.length ? `${path}?${remaining.join('&')}` : path;
}

/** 将视频截帧云处理参数插入 query 首段，避免落在 OSS/STS 签名参数之后。 */
function prependVideoSnapshotQuery(url: string, snapshotSegment: string): string {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) return `${url}?${snapshotSegment}`;
  const path = url.slice(0, queryIndex);
  const query = url.slice(queryIndex + 1);
  return `${path}?${snapshotSegment}&${query}`;
}

/** 将截帧偏移格式化为七牛 vframe 可识别的秒数。 */
function formatVframeOffsetSeconds(offsetSeconds: number): number {
  const safeOffset = Math.max(0, offsetSeconds);
  return parseFloat(safeOffset.toFixed(3));
}

/** 按秒数偏移获取视频截帧图片 URL。 */
export function getVideoFrameUrl(url?: string, offsetSeconds = 0): string {
  if (!url) return '';
  const sourceUrl = stripVideoFrameCommand(normalizeVideoCoverSourceUrl(url));
  const offset = formatVframeOffsetSeconds(offsetSeconds);
  return prependVideoSnapshotQuery(sourceUrl, `vframe/jpg/offset/${offset}`);
}

/** 解析视频展示封面，保留服务端已签名的图片/截帧预设，原视频地址才转换成首帧图片。 */
export function resolveVideoCoverUrl(storedCoverUrl?: string, fallbackVideoUrl?: string): string {
  const cover = storedCoverUrl?.trim() || '';
  const fallback = fallbackVideoUrl?.trim() || '';
  const candidate = cover || fallback;
  if (!candidate) return '';

  // 保留已处理封面的预设、偏移、尺寸及签名；仅旧数据把 vframe 放在鉴权参数末尾时修正顺序。
  if (/!vframeOffset[\d.]+(?:[?#]|$)/i.test(candidate) || /\?vframe\//i.test(candidate)) return candidate;

  const pathOnly = candidate.split(/[?#]/)[0].split('!')[0].toLowerCase();
  const isVideoFile = /\.(mp4|mov|avi|mkv|flv|wmv|webm|m4v)$/.test(pathOnly);
  if (isVideoFile || hasVideoFrameCommand(candidate)) {
    return getVideoFrameUrl(candidate, 0);
  }
  // 旧资产会把原视频地址回填为封面；无扩展名的视频也应走缺封面的首帧兜底。
  if (cover && cover !== fallback) return cover;
  return getVideoFrameUrl(fallback, 0);
}

/** 获取视频首帧封面图。 */
export function getVideoCoverUrl(url?: string): string {
  return getVideoFrameUrl(url, 0);
}
